import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    console.log('AuthCallback component mounted');
    console.log('Current URL:', window.location.href);

    const handleAuthCallback = async () => {
      try {
        // Check initial session
        const { data: { session: initialSession }, error: initialError } = await supabase.auth.getSession();
        console.log('Initial Session:', initialSession, 'Initial Error:', initialError);

        // Extract tokens from URL hash
        const params = new URLSearchParams(window.location.search);
const accessToken = params.get('access_token');
const refreshToken = params.get('refresh_token');

        console.log('Access Token:', accessToken);
        console.log('Refresh Token:', refreshToken);

        if (accessToken && refreshToken) {
          // Set the session with tokens from the URL
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (setSessionError) {
            throw new Error('Error setting session: ' + setSessionError.message);
          }

          // Verify the session
          const { data: { session }, error: getSessionError } = await supabase.auth.getSession();
          console.log('Verified Session:', session, 'Session Error:', getSessionError);

          if (getSessionError) {
            throw new Error('Error getting session: ' + getSessionError.message);
          }

          if (session?.user) {
            const user = session.user;
            console.log('User:', user);

            // Check if profile exists
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('id')
              .eq('id', user.id)
              .single();

            console.log('Profile:', profile, 'Profile Error:', profileError);

            if (profileError && profileError.code !== 'PGRST116') {
              throw new Error('Error checking profile: ' + profileError.message);
            }

            if (!profile) {
              // Create initial profile with Google data
              const { error: insertError } = await supabase
                .from('profiles')
                .insert({
                  id: user.id,
                  full_name: user.user_metadata?.full_name || user.user_metadata?.name || 'Google User',
                  email: user.email,
                  phone: null,
                  address: null,
                  created_at: new Date().toISOString(),
                  role: 'user',
                });

              if (insertError) {
                throw new Error('Error creating profile: ' + insertError.message);
              }
              console.log('Profile created successfully');
            }

            // Clean up URL
            window.history.replaceState({}, document.title, '/auth/callback');
            toast.success('Please complete your profile');
            navigate('/complete-profile');
          } else {
            throw new Error('No user found in session');
          }
        } else {
          console.log('No tokens in URL, falling back to initial session or auth state...');
          if (initialSession?.user) {
            const user = initialSession.user;
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('id')
              .eq('id', user.id)
              .single();

            if (profileError && profileError.code !== 'PGRST116') {
              throw new Error('Error checking profile: ' + profileError.message);
            }

            if (!profile) {
              const { error: insertError } = await supabase
                .from('profiles')
                .insert({
                  id: user.id,
                  full_name: user.user_metadata?.full_name || user.user_metadata?.name || 'Google User',
                  email: user.email,
                  phone: null,
                  address: null,
                  created_at: new Date().toISOString(),
                  role: 'user',
                });

              if (insertError) {
                throw new Error('Error creating profile: ' + insertError.message);
              }
            }

            window.history.replaceState({}, document.title, '/auth/callback');
            toast.success('Please complete your profile');
            navigate('/complete-profile');
          } else {
            // Listen for auth state changes
            console.log('Listening for auth state change...');
            const { data: authListener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
              console.log('Auth Event:', event, 'New Session:', newSession);
              if (event === 'SIGNED_IN' && newSession?.user) {
                const user = newSession.user;
                const { data: profile, error: profileError } = await supabase
                  .from('profiles')
                  .select('id')
                  .eq('id', user.id)
                  .single();

                if (profileError && profileError.code !== 'PGRST116') {
                  toast.error('Error checking profile: ' + profileError.message);
                  navigate('/signin');
                  return;
                }

                if (!profile) {
                  const { error: insertError } = await supabase
                    .from('profiles')
                    .insert({
                      id: user.id,
                      full_name: user.user_metadata?.full_name || user.user_metadata?.name || 'Google User',
                      email: user.email,
                      phone: null,
                      address: null,
                      created_at: new Date().toISOString(),
                      role: 'user',
                    });

                  if (insertError) {
                    toast.error('Error creating profile: ' + insertError.message);
                    navigate('/signin');
                    return;
                  }
                }

                window.history.replaceState({}, document.title, '/auth/callback');
                toast.success('Please complete your profile');
                navigate('/complete-profile');
              }
            });

            return () => {
              authListener.subscription.unsubscribe();
            };
          }
        }
      } catch (error: any) {
        console.error('Error in auth callback:', error);
        toast.error(error.message);
        navigate('/signin');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Completing sign in...</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Please wait while we redirect you.</p>
      </div>
    </div>
  );
};

export default AuthCallback;